import { Text, useToast, VStack } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router';

import {
  parse,
  ValleyBankLoanAccountWithCollateralAndStats,
} from '@finley-technologies/valley-bank-utils';

import { useGetValleyBankLoanAccountQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { useCollateralAccountsSelector } from '../CollateralAccountsSelectorProvider';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import {
  Alert,
  ProgressAlert,
} from '~/packages/common-frontend/components/Alert';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { ArrangementGroupTag } from '~/valleyBank/domains/ArrangementGroups/hooks/ArrangementGroupsContext';
import { getArrangementGroupNumbers } from '~/valleyBank/domains/Collateral/ui/CollateralAccountArrangementGroupNumbers';
import {
  CustomerCifNumTag,
  CustomerTag,
} from '~/valleyBank/domains/Customers/hooks/CustomerContext';
import { useLoanAccountUrlParam } from '~/valleyBank/domains/LoanAccounts/hooks/LoanAccountContext';
import { LoanAccountNotFoundAlert } from '~/valleyBank/domains/LoanAccounts/ui/Details/LoanAccountNotFoundAlert';
import { LoanAccountsDetailsTemplate } from '~/valleyBank/domains/LoanAccounts/ui/Details/LoanAccountsDetailsTemplate';
import { LoanSummary } from '~/valleyBank/domains/LoanAccounts/ui/Details/OverviewLoanSummary';
import { useDataMetadataContext } from '~/valleyBank/domains/page/hooks/DataMetadataContext';
import { useUserRoleTypePermissionsContext } from '~/valleyBank/domains/page/hooks/useUserRoleTypePermissions';

// Fragments
const RouteBase = () => {
  const {
    setSelectedCollateralAccounts,
    selectedFinleyReceivableIds,
    setSelectedFinleyReceivableIds,
    queuedSelectedCollateralAccounts,
    setQueuedSelectedCollateralAccounts,
    queuedSelectedFinleyReceivableIds,
    setQueuedSelectedFinleyReceivableIds,
    isFilterApplied,
    setIsFilterApplied,
    setInitialSelections,
    getDisplayRefetchAlert,
  } = useCollateralAccountsSelector();

  const toast = useToast();
  const { permissions } = useUserRoleTypePermissionsContext();
  const {
    reportingDate,
    isPreview,
    dashboardMode,
    setAsOfDate,
  } = useDataMetadataContext();
  const { loanAccountId } = useLoanAccountUrlParam();
  const [
    loanAccount,
    setLoanAccount,
  ] = useState<ValleyBankLoanAccountWithCollateralAndStats | null>(null);
  const [
    isLoanAccountLoadingOrParsing,
    setIsLoanAccountLoadingOrParsing,
  ] = useState<boolean>(true); // account for the time it takes between loanAccount data returning and parsing + setting the data into the loanAccount state
  const [arrangementGroup, setArrangementGroup] = useState<string | null>(null);
  const [borrower, setBorrower] = useState<
    ValleyBankLoanAccountWithCollateralAndStats['loanParties'][number] | null
  >(null);

  const {
    loading: isLoading,
    error: isError,
    refetch: refetchValleyBankLoanAccount,
  } = useGetValleyBankLoanAccountQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      setIsLoanAccountLoadingOrParsing(false);
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      reportingDate,
      isPreview,
      loanAccountNumber: loanAccountId || '',
      selectedFinleyReceivableIds:
        isFilterApplied && selectedFinleyReceivableIds
          ? Array.from(selectedFinleyReceivableIds)
          : undefined,
    },
    onCompleted: (res) => {
      setIsLoanAccountLoadingOrParsing(false);

      try {
        const loanAccountJson = res.valleyBankLoanAccount?.loanAccount;
        const newLoanAccount = parse.parseValleyBankLoanAccountWithCollateralAndStats(
          loanAccountJson ?? '',
        );
        if (newLoanAccount) {
          const currentBorrower =
            newLoanAccount.loanParties.find(
              ({ cif_num: cifNum }) => cifNum === newLoanAccount.cifNum,
            ) || null;
          if (!newLoanAccount.arrangementGroupId) {
            const arrangementGroupNumbers = getArrangementGroupNumbers(
              newLoanAccount.collateralAccounts.flatMap(
                ({ receivables }) => receivables,
              ),
            );
            setArrangementGroup(arrangementGroupNumbers[0] || null);
          } else {
            setArrangementGroup(newLoanAccount.arrangementGroupId);
          }
          setBorrower(currentBorrower);
          setAsOfDate(newLoanAccount.asOfDate);
          setLoanAccount(newLoanAccount);
        }
      } catch (err) {
        Sentry.captureException(err);
      }
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    setInitialSelections(loanAccount);
  }, [loanAccount, setInitialSelections]);

  const toggleCollateralSelect = useCallback(
    ({
      collateralAccountNumber,
      isChecked,
    }: {
      collateralAccountNumber: string;
      isChecked: boolean;
    }) => {
      const newSelectedCollateralAccounts = queuedSelectedCollateralAccounts
        ? new Set([...queuedSelectedCollateralAccounts])
        : new Set<string>();
      const newSelectedFinleyReceivableIds = queuedSelectedFinleyReceivableIds
        ? new Set([...queuedSelectedFinleyReceivableIds])
        : new Set<string>();

      const collateralAccountDetails = loanAccount?.collateralAccounts.find(
        (collateralAccount) =>
          collateralAccount.accountNumber === collateralAccountNumber,
      );

      // add or remove collateral account and receivable ids from their respective selection states
      if (!isChecked) {
        newSelectedCollateralAccounts.delete(collateralAccountNumber);

        if (collateralAccountDetails) {
          collateralAccountDetails.receivables.forEach((receivable) => {
            newSelectedFinleyReceivableIds.delete(
              receivable.finley_receivable_id,
            );
          });
        }
      } else {
        newSelectedCollateralAccounts.add(collateralAccountNumber);

        if (collateralAccountDetails) {
          collateralAccountDetails.receivables.forEach((receivable) => {
            newSelectedFinleyReceivableIds.add(receivable.finley_receivable_id);
          });
        }
      }

      setQueuedSelectedCollateralAccounts(newSelectedCollateralAccounts);
      setQueuedSelectedFinleyReceivableIds(newSelectedFinleyReceivableIds);
    },
    [
      loanAccount?.collateralAccounts,
      setQueuedSelectedCollateralAccounts,
      setQueuedSelectedFinleyReceivableIds,
      queuedSelectedCollateralAccounts,
      queuedSelectedFinleyReceivableIds,
    ],
  );

  const refetchLoanAccount = useCallback(() => {
    refetchValleyBankLoanAccount({
      reportingDate,
      isPreview,
      loanAccountNumber: loanAccountId || '',
      selectedFinleyReceivableIds:
        queuedSelectedFinleyReceivableIds &&
        Array.from(queuedSelectedFinleyReceivableIds),
    });
    setIsFilterApplied(true);
    // promote the queued to selected
    setSelectedCollateralAccounts(queuedSelectedCollateralAccounts);
    setSelectedFinleyReceivableIds(queuedSelectedFinleyReceivableIds);
  }, [
    isPreview,
    loanAccountId,
    reportingDate,
    refetchValleyBankLoanAccount,
    setIsFilterApplied,
    setSelectedCollateralAccounts,
    setSelectedFinleyReceivableIds,
    queuedSelectedCollateralAccounts,
    queuedSelectedFinleyReceivableIds,
  ]);

  const displayRefetchAlert = useMemo(
    () => getDisplayRefetchAlert({ loanAccount, isLoading }),
    [getDisplayRefetchAlert, isLoading, loanAccount],
  );

  return (
    <>
      <LoanAccountsDetailsTemplate
        selectedTab="summary"
        headerChildren={
          borrower && (
            <>
              <CustomerCifNumTag cifnum={borrower.cif_num} />
              <CustomerTag
                cifnum={borrower.cif_num}
                label={`Borrower: ${borrower.full_name} (${borrower.cif_num})`}
              />
              <ArrangementGroupTag arrangmentGroup={arrangementGroup} />
            </>
          )
        }
        refetchLoanAccount={refetchLoanAccount}
        displayRefetchAlert={displayRefetchAlert}
      >
        <VStack spacing="32px">
          {isLoading && !loanAccount && (
            <ProgressAlert>Loading loan account</ProgressAlert>
          )}
          {!isLoading && !loanAccount && isError && (
            <Alert>
              <Text>Error loading loan account</Text>
            </Alert>
          )}
          {!isLoanAccountLoadingOrParsing && !isLoading && !loanAccount && (
            <LoanAccountNotFoundAlert />
          )}
          {loanAccount && (
            <LoanSummary
              canUnassign={
                dashboardMode === 'current' &&
                permissions.assignmentQueueRead &&
                permissions.assignmentQueueEdit
              }
              loanAccount={loanAccount}
              queuedSelectedCollateralAccounts={
                queuedSelectedCollateralAccounts
              }
              toggleCollateralSelect={toggleCollateralSelect}
              isLoading={isLoading}
              isError={Boolean(isError)}
            />
          )}
        </VStack>
      </LoanAccountsDetailsTemplate>
    </>
  );
};

// Exports
export const Route = () => (
  <RouteErrorBoundary
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <Navigate to="../" />;
    }}
  >
    <RouteBase />
  </RouteErrorBoundary>
);
